import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Badge,
  Stack,
} from "@mui/material";
import {
  ViewList as ViewListIcon,
  Add as AddIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";

// Import your existing components
import MyGLNsView from "./MyGLNsView";
import CreateGLNView from "./CreateGLNView";

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`gln-tabpanel-${index}`}
      aria-labelledby={`gln-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Accessibility props for tabs
function a11yProps(index) {
  return {
    id: `gln-tab-${index}`,
    'aria-controls': `gln-tabpanel-${index}`,
  };
}

const GLNManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  // You can pass data between tabs if needed
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Function to trigger refresh of MyGLNs when a new GLN is created
  const handleGLNCreated = () => {
    setRefreshTrigger(prev => prev + 1);
    setActiveTab(0); // Switch back to MyGLNs tab after creation
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f8fafc', mt: 8 }}>
      {/* Header Section */}
      <Paper 
        elevation={2} 
        sx={{ 
          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
          color: "white",
          borderRadius: 0,
          mb: 0
        }}
      >
        <Box sx={{ padding: "2em 2em 1em 2em" }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              backgroundColor: "rgba(255,255,255,0.2)"
            }}>
              <BusinessIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                GLN Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage your Global Location Numbers - View existing GLNs or create new ones
              </Typography>
            </Box>
          </Stack>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="secondary"
            textColor="inherit"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 64,
                textTransform: 'none',
                color: 'rgba(255,255,255,0.8)',
                '&.Mui-selected': {
                  color: 'white',
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px 8px 0 0',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4ade80',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTabs-flexContainer': {
                gap: 1,
              }
            }}
          >
            <Tab
              icon={<ViewListIcon />}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>My GLNs</Typography>
                  {/* You can add a badge here showing count if needed */}
                  {/* <Badge badgeContent={totalCount} color="secondary" sx={{ '& .MuiBadge-badge': { backgroundColor: '#4ade80', color: '#1f2937' } }} /> */}
                </Stack>
              }
              iconPosition="start"
              {...a11yProps(0)}
            />
            <Tab
              icon={<AddIcon />}
              label="Create New GLN"
              iconPosition="start"
              {...a11yProps(1)}
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>
        <MyGLNsView 
          key={refreshTrigger} // Force refresh when new GLN is created
          onSwitchToCreate={() => setActiveTab(1)} // Allow switching to create tab
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <CreateGLNView 
          onGLNCreated={handleGLNCreated} // Callback when GLN is created
          onCancel={() => setActiveTab(0)} // Handle cancel action
        />
      </TabPanel>
    </Box>
  );
};

export default GLNManagement;